import { VStack } from '@terra/ui-styled-system';
import {
  AlertBanner,
  SettingsBooleanControl,
  SettingsRow,
} from '@terra/ui-design-system';
import { useTranslations } from 'next-intl';
import { useSettingsHelpers } from '../hooks/useSettingsHelpers';
import {
  DEFAULT_UPSTREAM_PROXY_PORT,
  MAX_PROXY_PORT,
  MIN_PROXY_PORT,
} from '../constants/proxy-port.constants';
import { useInternalNetworkTunnel } from '../hooks/useInternalNetworkTunnel';
import { useInternalNetworkTunnelVisibility } from '../hooks/useInternalNetworkTunnelVisibility';
import { isInternalNetworkTunnelRoutingTorch } from '../electron-shared/utils/internal-network-tunnel-routing';
import { InternalNetworkTunnelSettingsTab } from './settings/InternalNetworkTunnelSettingsTab';
import { ProxySettingsGroup } from './settings/proxy-settings/ProxySettingsGroup';
import { ProxySettingEditRow } from './settings/proxy-settings/ProxySettingEditRow';

export function UpstreamTab() {
  const t = useTranslations('settings.proxyForm.upstream');
  const tGeneral = useTranslations('settings.proxyForm.general');
  const tTunnel = useTranslations('settings.internalNetworkTunnel');
  const { settings, updateSetting, updateNestedSetting } = useSettingsHelpers();
  const tunnel = useInternalNetworkTunnel();
  const showInternalNetworkTunnel = useInternalNetworkTunnelVisibility();
  const tunnelRoutingTorch =
    showInternalNetworkTunnel &&
    isInternalNetworkTunnelRoutingTorch(tunnel.status);
  const upstreamPausedByTunnel = settings.upstreamEnabled && tunnelRoutingTorch;

  return (
    <VStack gap={6} alignItems="stretch" w="full">
      {upstreamPausedByTunnel ? (
        <AlertBanner
          kind="attention"
          type="outline"
          title={tTunnel('exclusiveWithUpstreamTitle')}
          description={tTunnel('exclusiveWithUpstream')}
        />
      ) : null}

      <ProxySettingsGroup>
        <SettingsRow
          label={t('enabled')}
          description={
            tunnelRoutingTorch
              ? tTunnel('upstreamPausedByTunnel')
              : t('enabledDescription')
          }
          control={
            <SettingsBooleanControl
              checked={settings.upstreamEnabled}
              disabled={tunnelRoutingTorch}
              onCheckedChange={(details) =>
                updateSetting('upstreamEnabled', details.checked)
              }
            />
          }
        />
        <SettingsRow
          label={t('validateTls')}
          description={t('validateTlsDescription')}
          control={
            <SettingsBooleanControl
              checked={settings.validateUpstreamTls}
              onCheckedChange={(details) =>
                updateSetting('validateUpstreamTls', details.checked)
              }
            />
          }
        />
      </ProxySettingsGroup>

      {settings.upstreamEnabled ? (
        <ProxySettingsGroup title={t('connectionGroup')}>
          <ProxySettingEditRow
            kind="text"
            label={t('host')}
            description={t('hostDescription')}
            value={settings.upstreamHost || ''}
            onSave={(value) => updateSetting('upstreamHost', value)}
            placeholder="proxy.example.com"
          />
          <ProxySettingEditRow
            kind="integer"
            label={t('port')}
            description={tGeneral('portDescription', {
              min: MIN_PROXY_PORT,
              max: MAX_PROXY_PORT,
            })}
            value={settings.upstreamPort ?? DEFAULT_UPSTREAM_PROXY_PORT}
            min={MIN_PROXY_PORT}
            max={MAX_PROXY_PORT}
            onSave={(value) => updateSetting('upstreamPort', value)}
            placeholder={String(DEFAULT_UPSTREAM_PROXY_PORT)}
          />
        </ProxySettingsGroup>
      ) : null}

      {settings.upstreamEnabled ? (
        <ProxySettingsGroup title={t('authGroup')}>
          <ProxySettingEditRow
            kind="text"
            label={t('username')}
            value={settings.upstreamAuth?.username || ''}
            onSave={(value) =>
              updateNestedSetting('upstreamAuth', 'username', value)
            }
            placeholder="username"
          />
          <ProxySettingEditRow
            kind="password"
            label={t('password')}
            value={settings.upstreamAuth?.password || ''}
            onSave={(value) =>
              updateNestedSetting('upstreamAuth', 'password', value)
            }
            placeholder="password"
          />
        </ProxySettingsGroup>
      ) : null}

      {showInternalNetworkTunnel ? (
        <InternalNetworkTunnelSettingsTab
          corporateUpstreamEnabled={settings.upstreamEnabled}
          {...tunnel}
        />
      ) : null}
    </VStack>
  );
}
